import DialogProvider from "@components/dialog_provider"
import TextInput from "@components/text_input"
import { createCollageTemplateSetReq } from "@domain/collage"
import { yupResolver } from "@hookform/resolvers/yup"
import useControlledForm from "@hooks/use_controlled_form"
import useTranslate from "@hooks/use_translate"
import { Button, Stack, Typography } from "@mui/material"
import { showErrorToast, showSuccessToast } from "@utils/toast"
import { useState } from "react"
import * as yup from "yup"

type CreateCollageTemplateSetDialogProps = {
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
}

type CreateCollageTemplateSetFormData = {
  customerId: string
  cPersonId: string
  folderName: string
}

const schema = yup
  .object({
    customerId: yup.string().required(),
    cPersonId: yup.string().required(),
    folderName: yup.string().required()
  })
  .required()

export default function CreateCollageTemplateSetDialog({
  isOpen,
  setIsOpen
}: CreateCollageTemplateSetDialogProps) {
  const { translate } = useTranslate()
  const [loading, setLoading] = useState(false)
  const [secondaryLogo, setSecondaryLogo] = useState<File | null>(null)

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    trigger,
    formState: { isValid }
  } = useControlledForm<CreateCollageTemplateSetFormData>({
    resolver: yupResolver(schema),
    mode: "all"
  })

  const setClose = () => {
    setIsOpen(false)
  }

  const handleClose = () => {
    setIsOpen(false)
    setSecondaryLogo(null)
    reset()
  }

  const onFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ""
    if (!file) {
      return
    }
    setSecondaryLogo(file)
  }

  const onSubmit = async (data: CreateCollageTemplateSetFormData) => {
    if (!secondaryLogo) {
      showErrorToast("Please select a secondary logo image")
      return
    }

    setLoading(true)
    const { isSuccess, errorMessage } = await createCollageTemplateSetReq({
      customerId: data.customerId,
      cPersonId: data.cPersonId,
      folderName: data.folderName,
      secondaryLogo
    })

    if (!isSuccess) {
      setLoading(false)
      showErrorToast(String(errorMessage))
      return
    }

    showSuccessToast(translate("succesfully_added"))
    setLoading(false)
    handleClose()
  }

  return (
    <DialogProvider
      title="Create Collage Template Set"
      leftButton={{
        text: translate("cancel"),
        color: "secondary",
        onClick: setClose,
        disabled: loading
      }}
      rightButton={{
        text: translate("create"),
        color: "primary",
        onClick: handleSubmit(onSubmit),
        loading: loading,
        disabled: !isValid || !secondaryLogo || loading
      }}
      isOpen={isOpen}
      setClose={handleClose}
      size="small"
    >
      <Stack gap={1}>
        <Typography variant="body2" color="text.secondary">
          Creates the 5 default collage templates (5&apos;li, 4&apos;lü,
          3&apos;lü, 2&apos;li, 1&apos;li) and their grouping folder for the
          given customer.
        </Typography>
        <TextInput<CreateCollageTemplateSetFormData>
          label="Customer ID"
          disabled={loading}
          name="customerId"
          placeholder={translate("enter")}
          control={control}
          onChange={(e) => {
            setValue("customerId", e.target.value)
            trigger("customerId")
          }}
          required
        />
        <TextInput<CreateCollageTemplateSetFormData>
          label="Customer Person ID"
          disabled={loading}
          name="cPersonId"
          placeholder={translate("enter")}
          control={control}
          onChange={(e) => {
            setValue("cPersonId", e.target.value)
            trigger("cPersonId")
          }}
          required
        />
        <TextInput<CreateCollageTemplateSetFormData>
          label="Folder Name"
          disabled={loading}
          name="folderName"
          placeholder={translate("enter")}
          control={control}
          onChange={(e) => {
            setValue("folderName", e.target.value)
            trigger("folderName")
          }}
          required
        />
        <Button
          variant="outlined"
          component="label"
          disabled={loading}
          sx={{ mt: 1 }}
        >
          {secondaryLogo ? secondaryLogo.name : "Select secondary logo"}
          <input
            hidden
            type="file"
            accept="image/png,image/jpeg"
            onChange={onFileSelect}
          />
        </Button>
      </Stack>
    </DialogProvider>
  )
}
